import * as React from 'react'
import * as Redux from 'react-redux'

import { t } from '@owl-nest/localize'
import * as api from '@owl-nest/api-client/latest'
import { illustrations, styles as S } from '@ulule/owl-kit-components/next'
import * as model from '@owl-nest/models'

import { SponsorshipTag } from '../../../components/SponsorshipTag'
import { RootState } from '../../../../../../reducers'

import { EventProps } from './type'
import { EventBase } from './Base'
import { User } from '../component/User'

export function ProjectStart({
  event,
}: EventProps<api.EventProjectStart>): React.ReactElement<EventProps<api.EventProjectStart>> {
  return <EventBase date={event.time} title={t('Project started')} />
}

export function ProjectEnd({
  event,
}: EventProps<api.EventProjectEnd>): React.ReactElement<EventProps<api.EventProjectEnd>> {
  if (event.data.has_extra_time) {
    return <EventBase date={event.time} title={t('Extended campaign')} />
  } else if (event.data?.goal_status === api.EventGoalStatus.SUCCESS) {
    return (
      <EventBase
        done
        date={event.time}
        title={t('Project ended')}
        icon={<illustrations.twoToned.HeartWithConfetti size={40} inverted noBackground />}
      />
    )
  }

  return <EventBase date={event.time} title={t('Project ended')} />
}

export function ProjectEndExtratime({
  event,
}: EventProps<api.EventProjectEndExtraTime>): React.ReactElement<EventProps<api.EventProjectEndExtraTime>> {
  return (
    <EventBase
      done
      date={event.time}
      title={t('Project ended')}
      icon={<illustrations.twoToned.HeartWithConfetti size={40} inverted noBackground />}
    />
  )
}

export function GoalMet({
  event,
}: EventProps<api.EventPercentRaised>): React.ReactElement<EventProps<api.EventPercentRaised>> {
  return (
    <EventBase
      done
      date={event.time}
      title={t('%(percent)s raised', { percent: `${event.data.percent}%` })}
      icon={<illustrations.twoToned.CrackedEgg size={36} inverted noBackground />}
    />
  )
}

export function PercentRaised({
  event,
}: EventProps<api.EventPercentRaised>): React.ReactElement<EventProps<api.EventPercentRaised>> {
  return <EventBase date={event.time} title={t('%(percent)s raised', { percent: `${event.data.percent}%` })} />
}

export function FirstOrder({
  event,
}: EventProps<api.EventFirstOrder>): React.ReactElement<EventProps<api.EventFirstOrder>> | null {
  if (!event.data.user) {
    return null
  }

  return <EventBase date={event.time} title={t('First backer')} backer={<User user={event.data.user} />} />
}

export function Sponsorship({
  event,
}: EventProps<api.EventSponsorship>): React.ReactElement<EventProps<api.EventSponsorship>> {
  const project = Redux.useSelector<RootState, api.Project>((state) => state.application.project.data)

  return (
    <EventBase
      date={event.time}
      title={t('New sponsor')}
      sponsor={
        <>
          <S.image.Avatar type="extra-small" src={model.sponsorship.avatar(event.data, 'small')} />
          <S.heading.XS>{event.data.sponsor.name}</S.heading.XS>
        </>
      }
      tag={<SponsorshipTag sponsorship={event.data} project={project} />}
    />
  )
}
